/**
 * Slide-over profile panel for the Company Directory.
 *
 * Shows the whitelisted, all-roles-safe view of a teammate:
 * name, title, department, email, phone, manager, reports, tenure.
 * Does NOT show compensation, personal email, or anything HR-only —
 * the backend ``/directory`` endpoints already strip those fields.
 */
import { Fragment, useEffect, useState } from "react";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { Link } from "react-router-dom";
import { X, Mail, Phone, MapPin, Calendar, Globe2 } from "lucide-react";
import { Avatar } from "@/components/ui/Avatar";
import { Badge } from "@/components/ui/Badge";
import { Skeleton } from "@/components/ui/Skeleton";
import {
  getDirectoryProfile,
  type DirectoryProfile,
} from "@/api/directory";
import { EMPLOYMENT_TYPE_LABELS } from "@/lib/constants";
import { formatDate, fullName } from "@/lib/utils";

interface Props {
  employeeId: string | null;
  onClose: () => void;
}

function tenure(hireDate: string): string {
  const start = new Date(hireDate);
  const now = new Date();
  let months =
    (now.getFullYear() - start.getFullYear()) * 12 +
    (now.getMonth() - start.getMonth());
  if (now.getDate() < start.getDate()) months -= 1;
  if (months < 1) return "Less than a month";
  const years = Math.floor(months / 12);
  const rem = months % 12;
  const parts: string[] = [];
  if (years > 0) parts.push(`${years} year${years === 1 ? "" : "s"}`);
  if (rem > 0) parts.push(`${rem} month${rem === 1 ? "" : "s"}`);
  return parts.join(", ");
}

export function DirectoryProfilePanel({ employeeId, onClose }: Props) {
  const [profile, setProfile] = useState<DirectoryProfile | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!employeeId) {
      setProfile(null);
      return;
    }
    setLoading(true);
    getDirectoryProfile(employeeId)
      .then(setProfile)
      .catch(() => setProfile(null))
      .finally(() => setLoading(false));
  }, [employeeId]);

  const open = !!employeeId;

  return (
    <Transition show={open} as={Fragment}>
      <Dialog onClose={onClose} className="relative z-50">
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
        </TransitionChild>

        <div className="fixed inset-y-0 right-0 flex max-w-full">
          <TransitionChild
            as={Fragment}
            enter="transform transition ease-out duration-300"
            enterFrom="translate-x-full"
            enterTo="translate-x-0"
            leave="transform transition ease-in duration-200"
            leaveFrom="translate-x-0"
            leaveTo="translate-x-full"
          >
            <DialogPanel className="w-screen max-w-md bg-surface shadow-xl flex flex-col h-full">
              <div className="flex items-center justify-between border-b border-border px-5 py-4">
                <DialogTitle className="text-sm font-medium text-text-muted uppercase tracking-wide">
                  Profile
                </DialogTitle>
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Close profile"
                  className="text-text-muted hover:text-text-primary transition-colors"
                >
                  <X className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-5 py-6 space-y-6">
                {loading || !profile ? (
                  <div className="space-y-4">
                    <Skeleton className="h-20 w-20 rounded-full" />
                    <Skeleton className="h-6 w-3/4" />
                    <Skeleton className="h-4 w-1/2" />
                    <Skeleton className="h-24 w-full" />
                  </div>
                ) : (
                  <>
                    {/* Header */}
                    <div className="flex flex-col items-center text-center">
                      <Avatar
                        firstName={profile.first_name}
                        lastName={profile.last_name}
                        src={profile.avatar_url}
                        size="xl"
                      />
                      <h3 className="mt-3 text-xl font-semibold font-heading text-text-primary">
                        {fullName(
                          profile.first_name,
                          profile.last_name,
                          profile.preferred_name,
                        )}
                      </h3>
                      {profile.pronouns && (
                        <p className="text-xs text-text-muted">
                          {profile.pronouns}
                        </p>
                      )}
                      <p className="text-sm text-text-primary mt-1">
                        {profile.title}
                      </p>
                      {profile.department && (
                        <p className="text-xs text-text-muted">
                          {profile.department.name}
                        </p>
                      )}
                      <div className="mt-2 flex items-center gap-2 flex-wrap justify-center">
                        <Badge variant="default">
                          {EMPLOYMENT_TYPE_LABELS[profile.employment_type] ||
                            profile.employment_type}
                        </Badge>
                        {profile.work_location && (
                          <Badge variant="default">
                            {profile.work_location}
                          </Badge>
                        )}
                      </div>
                    </div>

                    {/* Contact */}
                    <div className="space-y-2 text-sm">
                      {profile.work_email && (
                        <a
                          href={`mailto:${profile.work_email}`}
                          className="flex items-center gap-3 text-text-primary hover:text-accent transition-colors"
                        >
                          <Mail className="h-4 w-4 text-text-muted" />
                          <span className="truncate">{profile.work_email}</span>
                        </a>
                      )}
                      {profile.phone && (
                        <a
                          href={`tel:${profile.phone}`}
                          className="flex items-center gap-3 text-text-primary hover:text-accent transition-colors"
                        >
                          <Phone className="h-4 w-4 text-text-muted" />
                          <span>{profile.phone}</span>
                        </a>
                      )}
                      {profile.office_location && (
                        <div className="flex items-center gap-3 text-text-primary">
                          <MapPin className="h-4 w-4 text-text-muted" />
                          <span>{profile.office_location}</span>
                        </div>
                      )}
                      {profile.timezone && (
                        <div className="flex items-center gap-3 text-text-primary">
                          <Globe2 className="h-4 w-4 text-text-muted" />
                          <span>{profile.timezone}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-3 text-text-primary">
                        <Calendar className="h-4 w-4 text-text-muted" />
                        <span>
                          Joined {formatDate(profile.hire_date)} ·{" "}
                          <span className="text-text-muted">
                            {tenure(profile.hire_date)}
                          </span>
                        </span>
                      </div>
                    </div>

                    {/* Manager */}
                    {profile.manager && (
                      <div>
                        <h4 className="text-xs uppercase tracking-wide text-text-muted mb-2">
                          Manager
                        </h4>
                        <Link
                          to={`/directory?person=${profile.manager.id}`}
                          className="flex items-center gap-3 rounded-lg border border-border px-3 py-2 hover:border-accent/50 transition-colors"
                        >
                          <Avatar
                            firstName={profile.manager.first_name}
                            lastName={profile.manager.last_name}
                            src={null}
                            size="sm"
                          />
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-text-primary truncate">
                              {profile.manager.first_name}{" "}
                              {profile.manager.last_name}
                            </p>
                            <p className="text-xs text-text-muted truncate">
                              {profile.manager.title}
                            </p>
                          </div>
                        </Link>
                      </div>
                    )}

                    {/* Direct reports */}
                    {profile.direct_reports.length > 0 && (
                      <div>
                        <h4 className="text-xs uppercase tracking-wide text-text-muted mb-2">
                          Direct reports ({profile.direct_reports.length})
                        </h4>
                        <div className="space-y-2">
                          {profile.direct_reports.map((r) => (
                            <Link
                              key={r.id}
                              to={`/directory?person=${r.id}`}
                              className="flex items-center gap-3 rounded-lg border border-border px-3 py-2 hover:border-accent/50 transition-colors"
                            >
                              <Avatar
                                firstName={r.first_name}
                                lastName={r.last_name}
                                src={null}
                                size="sm"
                              />
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-text-primary truncate">
                                  {r.first_name} {r.last_name}
                                </p>
                                <p className="text-xs text-text-muted truncate">
                                  {r.title}
                                </p>
                              </div>
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="pt-2">
                      <Link
                        to={`/employees/${profile.id}`}
                        className="text-sm text-accent hover:underline"
                      >
                        View full profile →
                      </Link>
                    </div>
                  </>
                )}
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  );
}
